import React from 'react';
import { motion } from 'framer-motion';
import { Shield, Lock, FileText, Globe } from 'lucide-react';

const PrivacyPolicy = () => {
  const privacySections = [
    {
      icon: Shield,
      title: 'Information We Collect',
      content: 'At Beta Pixel Studio, we collect minimal personal information necessary to provide our services.',
      details: [
        'Name and contact information',
        'Email address',
        'Project-related details',
        'Payment information for service transactions',
        'Website usage data through standard analytics'
      ]
    },
    {
      icon: Lock,
      title: 'How We Use Your Information',
      content: 'We use the collected information for the following purposes:',
      details: [
        'Providing and managing our digital services',
        'Communicating about projects and services',
        'Processing payments',
        'Improving our customer experience',
        'Responding to customer inquiries'
      ]
    },
    {
      icon: FileText,
      title: 'Data Protection Commitment',
      content: 'We are committed to protecting your personal data:',
      details: [
        'Implement robust security measures',
        'Use encrypted connections for data transmission',
        'Limit access to personal information',
        'Regular security audits and updates',
        'Compliance with international data protection standards'
      ]
    }
  ];

  return (
    <div className="sm:mt-8 md:pt-6 lg:pt-8 pt-10">
    <div className="min-h-screen bg-gray-900 py-16 px-4">
      <div className="container mx-auto">
        <motion.h1 
          initial={{ opacity: 0, y: -50 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-4xl md:text-5xl text-center mb-12 font-bold text-transparent bg-clip-text bg-gradient-to-r from-purple-500 to-cyan-500"
        >
          Privacy Policy
        </motion.h1>

        <div className="max-w-4xl mx-auto space-y-8">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="bg-gray-800/50 rounded-xl p-6 border border-purple-500/30"
          >
            <h2 className="text-2xl font-semibold text-white mb-4">
              Welcome to Beta Pixel Studio's Privacy Policy
            </h2>
            <p className="text-gray-300 mb-4">
              At Beta Pixel Studio, we are committed to protecting your privacy and transparency in our data practices. 
              This policy outlines how we collect, use, and safeguard your personal information.
            </p>
          </motion.div>

          {privacySections.map((section, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: index * 0.2 }}
              className="bg-gray-800/50 rounded-xl p-6 border border-purple-500/30"
            >
              <div className="flex items-center mb-4">
                <section.icon className="text-cyan-500 mr-4" size={40} />
                <h3 className="text-2xl font-semibold text-white">{section.title}</h3>
              </div>
              <p className="text-gray-400 mb-4">{section.content}</p>
              <ul className="space-y-2 text-gray-300">
                {section.details.map((detail, idx) => (
                  <li key={idx} className="flex items-center">
                    <span className="w-2 h-2 mr-2 bg-cyan-500 rounded-full"></span>
                    {detail}
                  </li>
                ))}
              </ul>
            </motion.div>
          ))}

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="bg-gray-800/50 rounded-xl p-6 border border-purple-500/30"
          >
            <h2 className="text-2xl font-semibold text-white mb-4">
              Contact Us
            </h2>
            <p className="text-gray-300">
              If you have any questions about our Privacy Policy, please contact us at:
              <br />
              <span className="text-cyan-500">privacy@betapixelstudio.com</span>
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="bg-gray-800/50 rounded-xl p-6 border border-purple-500/30"
          >
            <h2 className="text-2xl font-semibold text-white mb-4">
              Consent and Updates
            </h2>
            <p className="text-gray-300">
              By using our services, you consent to our privacy policy. We may update this policy 
              periodically, and we recommend reviewing it regularly. Continued use of our services 
              after changes constitutes acceptance of the updated policy.
            </p>
          </motion.div>
        </div>
      </div>
    </div>
    </div>

  );
};

export default PrivacyPolicy;